import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { trackEvent } from '../lib/analytics';
import { buildCtaOpenAnalyticsParams } from '../components/support/supportAssistantLogic';

/**
 * MB-AI-SUPPORT-3 — Point d'ouverture unique de l'Assistant SUPERKAWA,
 * utilisable depuis n'importe quel CTA de l'app (section 3 du ticket).
 *
 * marketing : pilote l'état ouvert/fermé du widget flottant
 * (SupportAssistantWidget variant="floating", monté une seule fois dans
 * App.js) — jamais de second widget créé.
 *
 * os/pos/client : n'ouvre PAS un nouveau composant — déclenche le Help
 * Sheet existant de la surface courante (OSHelpSheet/POSHelpSheet/
 * ClientHelpSheet, qui embarquent déjà l'assistant, cf. MB-AI-SUPPORT-2C).
 * Chaque shell (OSShell, POSPage, AccountPage, ClientHeader) enregistre son
 * `setHelpOpen` existant via `registerHelpSheetOpener` — aucun state dupliqué,
 * la source de vérité reste le state local de chaque shell.
 */
const SupportAssistantContext = createContext(null);

export function SupportAssistantProvider({ children }) {
  const [floatingOpen, setFloatingOpen] = useState(false);
  const [pendingMessage, setPendingMessage] = useState('');
  const openersRef = useRef({ os: [], pos: [], client: [] });

  const registerHelpSheetOpener = useCallback((surface, setOpenFn) => {
    if (!openersRef.current[surface]) openersRef.current[surface] = [];
    openersRef.current[surface].push(setOpenFn);
    return () => {
      openersRef.current[surface] = openersRef.current[surface].filter((fn) => fn !== setOpenFn);
    };
  }, []);

  const openSupportAssistant = useCallback(({ surface, source, initialMessage } = {}) => {
    trackEvent(
      'support_assistant_cta_open',
      buildCtaOpenAnalyticsParams({ surface, source, pathname: window.location.pathname })
    );

    if (surface === 'marketing') {
      setPendingMessage(initialMessage || '');
      setFloatingOpen(true);
      return;
    }

    const openers = openersRef.current[surface] || [];
    const opener = openers[openers.length - 1];
    if (opener) opener(true);
  }, []);

  const consumePendingMessage = useCallback(() => {
    setPendingMessage('');
  }, []);

  const value = useMemo(
    () => ({
      floatingOpen,
      setFloatingOpen,
      pendingMessage,
      consumePendingMessage,
      registerHelpSheetOpener,
      openSupportAssistant,
    }),
    [floatingOpen, pendingMessage, consumePendingMessage, registerHelpSheetOpener, openSupportAssistant]
  );

  return <SupportAssistantContext.Provider value={value}>{children}</SupportAssistantContext.Provider>;
}

export function useSupportAssistant() {
  const ctx = useContext(SupportAssistantContext);
  if (!ctx) {
    throw new Error('useSupportAssistant must be used within a SupportAssistantProvider');
  }
  return ctx;
}
